import * as THREE from "three";

// 優勝の紙吹雪（Three.js）

// ---------- 設定
const CONFETTI_SETTINGS = {
	// SCSSの$w-bpと揃える
	mobileBreakpoint: 1023,

	// 降らせる時間（ミリ秒）
	duration: 36000,
	fadeOut: 1800,

	// 開始時のフェードイン
	intro: 2200,
	fadeInDuration: [450, 1100],

	// 総量
	amount: {
		desktop: 450,
		mobile: 360,
		density: 0.74,
	},

	// 横風 正で右、負で左
	wind: 1.2,

	// 手前のブラー
	foreground: {
		desktopBlur: 3,
		mobileBlur: 2,
		desktopOpacity: 0.86,
		mobileOpacity: 0.8,
		scale: 1.018,
	},

	// 縦方向のグラデーション
	goldGradient: [
		[0.0, "#c78630"],
		[0.14, "#e3b215"],
		[0.3, "#edba48"],
		[0.44, "#f4d38e"],
		[0.5, "#fff0bf"],
		[0.58, "#f2c96b"],
		[0.74, "#d99b2f"],
		[1.0, "#efbc41"],
	],

	// 中央の光沢
	sheenGradient: [
		[0, "rgba(205, 170, 114, 0.08)"],
		[0.28, "rgba(250, 227, 157, 0.12)"],
		[0.52, "rgba(255, 250, 226, 0.48)"],
		[0.72, "rgba(251, 234, 150, 0.14)"],
		[1, "rgba(213, 179, 104, 0.08)"],
	],

	camera: {
		fov: 46,
		z: 15,
		near: 0.1,
		far: 80,
	},

	// 上げるとGPU負荷が増える
	pixelRatio: {
		normalDesktop: 1.8,
		normalMobile: 1.45,
		foregroundDesktop: 1.25,
		foregroundMobile: 1.05,
	},

	// 生成範囲
	bounds: {
		x: [-10.5, 10.5],
		initialY: [-8, 12.5],
		respawnY: [8.5, 12.5],
		bottom: -8.5,
		wrapX: 11.5,
	},

	// 落下・揺れ・回転
	motion: {
		horizontalDrift: [-0.055, 0.055],
		sway: [0.18, 0.62],
		flutter: [0.55, 1.6],
		gustFrequency: 0.23,
		gustStrength: 0.11,
		turbulenceAmplitude: 0.18,
		turbulenceStrength: 0.07,
		depthFrequency: 0.22,
		depthStrength: 0.025,
		fallBase: 0.76,
		fallRotationInfluence: 0.24,
		airBrakeBase: 0.5,
		airBrakeRange: 0.42,
		spinPitchFactor: 0.22,
		spinYawFactor: 0.3,
		spinRollFactor: 0.55,
		paperSpinFactor: 0.42,
		ribbonSpinFactor: 1,
		gustSpinStrength: 0.24,
		maxFrameDelta: 0.035,
	},

	// 奥・中・手前の3層
	depthLayers: [
		{
			id: "far",
			scene: "back",
			share: 0.4,
			z: [-7, -2.2],
			scale: [0.42, 0.72],
			speed: [0.2, 0.44],
			spinMax: 0.6,
			windFactor: 0.1,
		},
		{
			id: "middle",
			scene: "back",
			share: 0.4,
			z: [-2.2, 3.2],
			scale: [0.76, 1.28],
			speed: [0.36, 0.76],
			spinMax: 1.05,
			windFactor: 0.16,
		},
		{
			id: "near",
			scene: "foreground",
			share: 0.2,
			z: [3.2, 8],
			scale: [1.45, 2.5],
			speed: [0.56, 1.04],
			spinMax: 1.45,
			windFactor: 0.22,
		},
	],

	// 紙片の種類
	shapes: [
		{
			kind: "paper",
			width: 0.14,
			height: 0.31,
			share: 0.36,
			curl: 0.025,
			wave: 0.01,
		},
		{
			kind: "paper",
			width: 0.09,
			height: 0.43,
			share: 0.25,
			curl: 0.04,
			wave: 0.014,
		},
		{
			kind: "paper",
			width: 0.2,
			height: 0.2,
			share: 0.16,
			curl: 0.018,
			wave: 0.008,
		},
		{
			kind: "ribbon",
			width: 0.075,
			height: 0.58,
			share: 0.23,
			turns: 1.35,
			radius: 0.085,
			segments: 18,
		},
	],
};

// ---------- 描画

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const { motion, bounds } = CONFETTI_SETTINGS;

const rand = ([min, max]) => min + Math.random() * (max - min);

let started = false;

const applyForegroundSettings = (compact) => {
	const rootStyle = document.documentElement.style;
	const { foreground } = CONFETTI_SETTINGS;

	rootStyle.setProperty("--confetti-fg-blur", `${compact ? foreground.mobileBlur : foreground.desktopBlur}px`);
	rootStyle.setProperty("--confetti-fg-opacity", String(compact ? foreground.mobileOpacity : foreground.desktopOpacity));
	rootStyle.setProperty("--confetti-fg-scale", String(foreground.scale));
};

const getPixelRatioLimit = (compact, foreground) => {
	const ratios = CONFETTI_SETTINGS.pixelRatio;

	if (foreground) {
		return compact ? ratios.foregroundMobile : ratios.foregroundDesktop;
	}

	return compact ? ratios.normalMobile : ratios.normalDesktop;
};

const getPixelRatio = (compact, foreground) => Math.min(window.devicePixelRatio || 1, getPixelRatioLimit(compact, foreground));

const createGoldTexture = () => {
	const canvas = document.createElement("canvas");
	canvas.width = 96;
	canvas.height = 384;
	const context = canvas.getContext("2d");

	const vertical = context.createLinearGradient(0, 0, 0, canvas.height);
	CONFETTI_SETTINGS.goldGradient.forEach(([position, color]) => {
		vertical.addColorStop(position, color);
	});
	context.fillStyle = vertical;
	context.fillRect(0, 0, canvas.width, canvas.height);

	const sheen = context.createLinearGradient(0, 0, canvas.width, 0);
	CONFETTI_SETTINGS.sheenGradient.forEach(([position, color]) => {
		sheen.addColorStop(position, color);
	});
	context.fillStyle = sheen;
	context.fillRect(0, 0, canvas.width, canvas.height);

	const texture = new THREE.CanvasTexture(canvas);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.minFilter = THREE.LinearFilter;
	texture.magFilter = THREE.LinearFilter;
	return texture;
};

const createPaperGeometry = (shape) => {
	const geometry = new THREE.PlaneGeometry(shape.width, shape.height, 1, 6);
	const { position } = geometry.attributes;

	for (let i = 0; i < position.count; i += 1) {
		const normalizedY = position.getY(i) / shape.height + 0.5;
		const bend = Math.sin(normalizedY * Math.PI) * shape.curl;
		const wave = Math.sin(normalizedY * Math.PI * 2) * shape.wave;
		position.setZ(i, bend + wave);
	}

	position.needsUpdate = true;
	geometry.computeVertexNormals();
	return geometry;
};

const createRibbonGeometry = (shape) => {
	const positions = [];
	const uvs = [];
	const indices = [];

	for (let i = 0; i <= shape.segments; i += 1) {
		const progress = i / shape.segments;
		const angle = progress * Math.PI * 2 * shape.turns;
		const centerX = Math.sin(angle) * shape.radius;
		const centerY = (progress - 0.5) * shape.height;
		const centerZ = Math.cos(angle) * shape.radius;
		const sideX = Math.cos(angle) * shape.width * 0.5;
		const sideZ = -Math.sin(angle) * shape.width * 0.5;

		positions.push(centerX - sideX, centerY, centerZ - sideZ);
		positions.push(centerX + sideX, centerY, centerZ + sideZ);
		uvs.push(0, progress, 1, progress);

		if (i < shape.segments) {
			const offset = i * 2;
			indices.push(offset, offset + 1, offset + 2);
			indices.push(offset + 1, offset + 3, offset + 2);
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setIndex(indices);
	geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
};

const createSpin = (layer, shape) => {
	const shapeFactor = shape.kind === "ribbon" ? motion.ribbonSpinFactor : motion.paperSpinFactor;

	return new THREE.Vector3(
		rand([-layer.spinMax * motion.spinPitchFactor * shapeFactor, layer.spinMax * motion.spinPitchFactor * shapeFactor]),
		rand([-layer.spinMax * motion.spinYawFactor * shapeFactor, layer.spinMax * motion.spinYawFactor * shapeFactor]),
		rand([-layer.spinMax * motion.spinRollFactor, layer.spinMax * motion.spinRollFactor]),
	);
};

/** スムーズステップ */
const smoothstep = (t) => t * t * (3 - 2 * t);

/** 開始フェードの不透明度 */
const getPieceOpacity = (piece, elapsedMs) => {
	if (piece.fadeDelay < 0) return 1;
	const t = (elapsedMs - piece.fadeDelay) / piece.fadeDuration;
	if (t <= 0) return 0;
	if (t >= 1) return 1;
	return smoothstep(t);
};

/** 枚ごとの不透明度をシェーダに乗せる */
const enableInstanceOpacity = (material) => {
	/* eslint-disable no-param-reassign -- Three.jsのマテリアル */
	material.transparent = true;
	material.depthWrite = false;
	material.onBeforeCompile = (shader) => {
		shader.vertexShader = shader.vertexShader
			.replace(
				"#include <common>",
				`#include <common>
attribute float instanceOpacity;
varying float vInstanceOpacity;`,
			)
			.replace(
				"#include <begin_vertex>",
				`#include <begin_vertex>
vInstanceOpacity = instanceOpacity;`,
			);

		shader.fragmentShader = shader.fragmentShader
			.replace(
				"#include <common>",
				`#include <common>
varying float vInstanceOpacity;`,
			)
			.replace(
				"#include <opaque_fragment>",
				`#include <opaque_fragment>
gl_FragColor.a *= vInstanceOpacity;`,
			);
	};
	material.customProgramCacheKey = () => "confetti-instance-opacity";
	/* eslint-enable no-param-reassign */
};

const createPiece = (index, layer, shape) => {
	const isRibbon = shape.kind === "ribbon";

	return {
		index,
		layer,
		shape,
		x: rand(bounds.x),
		y: rand(bounds.initialY),
		z: rand(layer.z),
		vx: rand(motion.horizontalDrift),
		vy: -rand(layer.speed),
		rotation: new THREE.Euler(isRibbon ? rand([-Math.PI, Math.PI]) : rand([-0.55, 0.55]), isRibbon ? rand([-Math.PI, Math.PI]) : rand([-0.7, 0.7]), rand([0, Math.PI * 2])),
		spin: createSpin(layer, shape),
		scale: rand(layer.scale),
		phase: rand([0, Math.PI * 2]),
		sway: rand(motion.sway),
		flutter: rand(motion.flutter),
		// ランダムなタイミングでフェードイン
		fadeDelay: Math.random() * CONFETTI_SETTINGS.intro,
		fadeDuration: rand(CONFETTI_SETTINGS.fadeInDuration),
	};
};

// 紙片を直接更新する
/* eslint-disable no-param-reassign */
const resetPiece = (piece) => {
	piece.x = rand(bounds.x);
	piece.y = rand(bounds.respawnY);
	piece.z = rand(piece.layer.z);
	piece.vx = rand(motion.horizontalDrift);
	piece.vy = -rand(piece.layer.speed);
	piece.scale = rand(piece.layer.scale);
	piece.spin.copy(createSpin(piece.layer, piece.shape));
	piece.phase = rand([0, Math.PI * 2]);
	// 下から戻った分はフェードなし
	piece.fadeDelay = -1;
};

const updatePiece = (piece, elapsed, delta, respawn) => {
	const gust = Math.sin(elapsed * motion.gustFrequency + piece.phase) * piece.sway;
	const turbulence = Math.sin(elapsed * piece.flutter + piece.phase * 1.7) * motion.turbulenceAmplitude;

	piece.x += (piece.vx + CONFETTI_SETTINGS.wind * piece.layer.windFactor + gust * motion.gustStrength + turbulence * motion.turbulenceStrength) * delta;
	piece.y += piece.vy * delta * (motion.fallBase + Math.abs(Math.cos(piece.rotation.x)) * motion.fallRotationInfluence);
	piece.z += Math.sin(elapsed * motion.depthFrequency + piece.phase) * motion.depthStrength * delta;

	const airBrake = motion.airBrakeBase + Math.abs(Math.sin(piece.rotation.x)) * motion.airBrakeRange;
	piece.rotation.x += piece.spin.x * airBrake * delta;
	piece.rotation.y += piece.spin.y * delta;
	piece.rotation.z += (piece.spin.z + gust * motion.gustSpinStrength) * delta;

	if (piece.x > bounds.wrapX) piece.x = -bounds.wrapX;
	if (piece.x < -bounds.wrapX) piece.x = bounds.wrapX;
	if (piece.y < bounds.bottom && respawn) resetPiece(piece);
};
/* eslint-enable no-param-reassign */

/** 紙吹雪を開始 */
export const celebrate = () => {
	if (started || prefersReducedMotion.matches) return;
	started = true;

	const isCompact = () => window.innerWidth <= CONFETTI_SETTINGS.mobileBreakpoint;
	let compact = isCompact();
	const maxPieces = compact ? CONFETTI_SETTINGS.amount.mobile : CONFETTI_SETTINGS.amount.desktop;

	const container = document.createElement("div");
	container.id = "confetti-canvas";
	container.setAttribute("aria-hidden", "true");
	document.body.appendChild(container);

	applyForegroundSettings(compact);

	const scene = new THREE.Scene();
	const foregroundScene = new THREE.Scene();
	const backScene = new THREE.Scene();
	const sceneMap = {
		normal: scene,
		foreground: foregroundScene,
		back: backScene,
	};

	const camera = new THREE.PerspectiveCamera(CONFETTI_SETTINGS.camera.fov, window.innerWidth / window.innerHeight, CONFETTI_SETTINGS.camera.near, CONFETTI_SETTINGS.camera.far);
	camera.position.set(0, 0, CONFETTI_SETTINGS.camera.z);

	const createRenderer = ({ foreground = false } = {}) => {
		const renderer = new THREE.WebGLRenderer({
			antialias: foreground ? false : !compact,
			alpha: true,
			powerPreference: "high-performance",
		});

		renderer.outputColorSpace = THREE.SRGBColorSpace;
		renderer.toneMapping = THREE.NoToneMapping;

		if (foreground) {
			renderer.domElement.classList.add("confetti-canvas__fg");
		}

		container.appendChild(renderer.domElement);
		return renderer;
	};

	const resizeRenderer = (renderer, isFg) => {
		renderer.setPixelRatio(getPixelRatio(compact, isFg));
		renderer.setSize(window.innerWidth, window.innerHeight);
	};

	const renderer = createRenderer();
	const foregroundRenderer = createRenderer({ foreground: true });
	resizeRenderer(renderer, false);
	resizeRenderer(foregroundRenderer, true);

	// 奥・中の層はコンテンツの背後に描く
	const backRenderer = new THREE.WebGLRenderer({
		antialias: !compact,
		alpha: true,
		preserveDrawingBuffer: true,
	});
	backRenderer.outputColorSpace = THREE.SRGBColorSpace;
	backRenderer.toneMapping = THREE.NoToneMapping;
	resizeRenderer(backRenderer, false);

	const backLayers = [...document.querySelectorAll(".contents .sec")].map((section) => {
		const canvas = document.createElement("canvas");
		canvas.className = "confetti-back";
		canvas.setAttribute("aria-hidden", "true");
		section.insertBefore(canvas, section.querySelector(".inner"));
		return {
			section,
			canvas,
			ctx: canvas.getContext("2d"),
			drawn: false,
		};
	});

	const drawBack = () => {
		const source = backRenderer.domElement;
		const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		backLayers.forEach((layer) => {
			const rect = layer.section.getBoundingClientRect();
			const visible = rect.bottom > 0 && rect.top < vh && rect.width > 0;
			const { canvas, ctx } = layer;

			if (!visible) {
				if (layer.drawn) {
					ctx.clearRect(0, 0, canvas.width, canvas.height);
					// eslint-disable-next-line no-param-reassign
					layer.drawn = false;
				}
				return;
			}

			const w = Math.ceil(rect.width * dpr);
			const h = Math.ceil(rect.height * dpr);
			if (canvas.width !== w || canvas.height !== h) {
				canvas.width = w;
				canvas.height = h;
			}

			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, rect.width, rect.height);
			ctx.drawImage(source, -rect.left, -rect.top, vw, vh);
			// eslint-disable-next-line no-param-reassign
			layer.drawn = true;
		});
	};

	const dummy = new THREE.Object3D();
	const goldTexture = createGoldTexture();
	const depthLayers = CONFETTI_SETTINGS.depthLayers.map((layer) => ({
		...layer,
		targetScene: sceneMap[layer.scene],
	}));

	const groups = [];
	const disposables = [goldTexture];

	CONFETTI_SETTINGS.shapes.forEach((shape) => {
		depthLayers.forEach((layer) => {
			const count = Math.max(1, Math.round(maxPieces * shape.share * layer.share * CONFETTI_SETTINGS.amount.density));
			const geometry = shape.kind === "ribbon" ? createRibbonGeometry(shape) : createPaperGeometry(shape);
			const opacities = new Float32Array(count);
			const instanceOpacity = new THREE.InstancedBufferAttribute(opacities, 1);
			instanceOpacity.setUsage(THREE.DynamicDrawUsage);
			geometry.setAttribute("instanceOpacity", instanceOpacity);

			const material = new THREE.MeshBasicMaterial({
				color: 0xffffff,
				map: goldTexture,
				side: THREE.DoubleSide,
				toneMapped: false,
			});
			enableInstanceOpacity(material);

			const mesh = new THREE.InstancedMesh(geometry, material, count);

			mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
			mesh.frustumCulled = false;
			layer.targetScene.add(mesh);

			disposables.push(geometry, material, mesh);

			groups.push({
				mesh,
				instanceOpacity,
				pieces: Array.from({ length: count }, (_, index) => createPiece(index, layer, shape)),
			});
		});
	});

	const applyPieceTransform = (piece, mesh, opacityAttr, elapsedMs) => {
		dummy.position.set(piece.x, piece.y, piece.z);
		dummy.rotation.copy(piece.rotation);
		dummy.scale.setScalar(piece.scale);
		dummy.updateMatrix();
		mesh.setMatrixAt(piece.index, dummy.matrix);
		opacityAttr.setX(piece.index, getPieceOpacity(piece, elapsedMs));
	};

	const updateAllTransforms = (elapsed, delta, respawn) => {
		const elapsedMs = elapsed * 1000;
		groups.forEach(({ mesh, instanceOpacity, pieces }) => {
			pieces.forEach((piece) => {
				updatePiece(piece, elapsed, delta, respawn);
				applyPieceTransform(piece, mesh, instanceOpacity, elapsedMs);
			});
			// バッファ更新フラグ
			// eslint-disable-next-line no-param-reassign
			mesh.instanceMatrix.needsUpdate = true;
			// eslint-disable-next-line no-param-reassign
			instanceOpacity.needsUpdate = true;
		});
	};

	groups.forEach(({ mesh, instanceOpacity, pieces }) => {
		pieces.forEach((piece) => applyPieceTransform(piece, mesh, instanceOpacity, 0));
		// eslint-disable-next-line no-param-reassign
		mesh.instanceMatrix.needsUpdate = true;
		// eslint-disable-next-line no-param-reassign
		instanceOpacity.needsUpdate = true;
	});

	let paused = prefersReducedMotion.matches;
	let respawn = true;
	let rafId = 0;
	const clock = new THREE.Clock();
	const endAt = Date.now() + CONFETTI_SETTINGS.duration;
	const removeAt = endAt + CONFETTI_SETTINGS.fadeOut;

	const handleResize = () => {
		compact = isCompact();
		camera.aspect = window.innerWidth / window.innerHeight;
		camera.updateProjectionMatrix();
		applyForegroundSettings(compact);
		resizeRenderer(renderer, false);
		resizeRenderer(foregroundRenderer, true);
		resizeRenderer(backRenderer, false);
	};

	const handleMotionChange = (event) => {
		paused = event.matches;
		clock.getDelta();
	};

	const cleanup = () => {
		cancelAnimationFrame(rafId);
		window.removeEventListener("resize", handleResize);
		prefersReducedMotion.removeEventListener("change", handleMotionChange);

		disposables.forEach((item) => {
			if (item.dispose) item.dispose();
		});
		renderer.dispose();
		foregroundRenderer.dispose();
		backRenderer.dispose();
		backLayers.forEach(({ canvas }) => canvas.remove());
		if (container.parentNode) container.parentNode.removeChild(container);

		document.documentElement.style.removeProperty("--confetti-fg-blur");
		document.documentElement.style.removeProperty("--confetti-fg-opacity");
		document.documentElement.style.removeProperty("--confetti-fg-scale");
	};

	const animate = () => {
		rafId = requestAnimationFrame(animate);
		const now = Date.now();

		if (now >= removeAt) {
			cleanup();
			return;
		}

		if (now >= endAt) {
			respawn = false;
			const t = (now - endAt) / CONFETTI_SETTINGS.fadeOut;
			const opacity = String(Math.max(0, 1 - t));
			container.style.opacity = opacity;
			backLayers.forEach(({ canvas }) => {
				// eslint-disable-next-line no-param-reassign
				canvas.style.opacity = opacity;
			});
		}

		const delta = Math.min(clock.getDelta(), motion.maxFrameDelta);

		if (!paused) {
			updateAllTransforms(clock.elapsedTime, delta, respawn);
		}

		renderer.render(scene, camera);
		foregroundRenderer.render(foregroundScene, camera);
		backRenderer.render(backScene, camera);
		drawBack();
	};

	window.addEventListener("resize", handleResize, { passive: true });
	prefersReducedMotion.addEventListener("change", handleMotionChange);

	animate();
};

export default celebrate;
